import { addToCart } from '../utils/cart';
import dispatchEvent from '../utils/dispatch-event';

let handled = false;
let previousCartItems = window.cart_items;
let isProcessingGifts = false; // Lock to prevent concurrent gift operations

addEventListener('cart:handle-promotions', () => {
  handlePromotions();
});

const isPromotionalItem = (id) => {
  const settings = window.buy_x_get_y_settings;
  if (!settings) {
    return false;
  }
  for (const promotion of settings) {
    if (promotion.x_id == id) {
      return true;
    }
  }
  return false;
};

const handlePromotions = (product_id = null, variant_id = null, product_cost = 0) => {
  
  // Prevent concurrent execution - wait for previous gift operations to complete
  if (isProcessingGifts) {
    return;
  }

  const settings = window.buy_x_get_y_settings;
  const cart_items = window.cart_items;
  let giftsToAdd = [];
  let giftQuantityUpdates = [];

  //if there are no settings or no cart items, we can return
  if (!settings || !cart_items) {
    return;
  }

  //if the cart items haven't changed, we can return
  let previous = groupItems(previousCartItems || []);
  let current = groupItems(cart_items || []);
  
  if(handled && !product_id){
    handled = noPromotionalItemsChanged(settings, current, previous);    
  }
  previousCartItems = [...window.cart_items];

  if (handled && !product_id) {
    hideDuplicateLineItemModifiers();
    return;
  }

  // ============================================================
  // MULTI-PASS APPROACH: Accumulate targets first, then reconcile
  // This fixes issues where multiple promotions share the same gift
  // ============================================================
  
  // Track cumulative target quantities per gift variant across ALL promotions
  // Key: variant_id, Value: { targetQuantity, inventoryLimit }
  const giftTargets = {};

  //loop through the settings for each promotion
  for (const promotion of settings) {
    //if there is no gift, we can continue
    if(promotion.gifts.length == 0){
      continue;
    }

    // Calculate qualifying_item_quantity once per promotion (shared across all gifts)
    let qualifying_item_quantity = 0;

    //if a product_id is passed in, we can check if it matches the x_id
    //if it does, we can increment the qualifying_item_quantity
    if(product_id != null){
      if(promotion.x_id == product_id){
        qualifying_item_quantity++;
      }
    }

    //loop through the cart items to find the qualifying item
    //if the item has a selling plan, we can continue
    //if the item matches the x_id, we can increment the qualifying_item_quantity
    for (const item of cart_items) {
      if(item.selling_plan_allocation?.selling_plan != null){
        continue;
      }

      const productMatch = item.product_id == promotion.x_id;
      const variantMatch = item.variant_id == promotion.x_id;
      
      if (productMatch || variantMatch) {
        qualifying_item_quantity += item.quantity;
      }
    }

    // Process each gift in the promotion (now supports multiple gifts)
    for (const gift of promotion.gifts) {
      
      // Skip if gift doesn't have a valid ID
      if (!gift || !gift.id) {
        continue;
      }
      
      const promoSettings = {
        amount_of_gifts_to_add: parseInt(gift.amount) || 1,
        cart_items,
        gift,
        gift_quantity: 0,
        max_gifts_per_order: parseInt(promotion.max_gifts_per_order) || 0,
        minimum_spend: promotion.minimum_spend,
        product_cost,
        product_id,
        qualifying_item_cost: promotion.qualifying_item_cost,
        qualifying_item_quantity: qualifying_item_quantity,
        x_id: promotion.x_id,
        y_inventory_quantity: parseInt(gift.inventory_quantity) || 0,
        y_variant_id: gift.id,
      };

      // Calculate target quantity for this promotion's gift (without adding to cart yet)
      let thisPromoTargetQuantity = 0;

      //if the qualifying_item_quantity is greater than 0, we can handle the promotion as a buy x get y
      if (promoSettings.qualifying_item_quantity > 0) {
        thisPromoTargetQuantity = calculateBuyXGetYTarget(promoSettings);
      }
      //if the minimum_spend is greater than 0 and the x_id is empty, we can handle the promotion as a spend x get y
      else if (promoSettings.minimum_spend > 0 && promoSettings.x_id == '') {
        thisPromoTargetQuantity = calculateSpendXGetYTarget(promoSettings);
      }

      // Accumulate target for this gift variant
      const variantKey = promoSettings.y_variant_id;
      if (!giftTargets[variantKey]) {
        giftTargets[variantKey] = {
          targetQuantity: 0,
          inventoryLimit: promoSettings.y_inventory_quantity,
        };
      }
      giftTargets[variantKey].targetQuantity += thisPromoTargetQuantity;
      // Use the lowest inventory limit if multiple promotions reference the same gift
      giftTargets[variantKey].inventoryLimit = Math.min(
        giftTargets[variantKey].inventoryLimit,
        promoSettings.y_inventory_quantity
      );
    }

  }

  // ============================================================
  // PASS 2: Reconcile cumulative targets with cart quantities
  // - Uses _promo_gift property to identify gift line items
  // - Maintains single line item per gift variant (update, not add duplicates)
  // ============================================================
  for (const [variantId, targets] of Object.entries(giftTargets)) {
    // Find ALL gift line items for this variant (there may be multiple due to Shopify quirks)
    const allGiftLineItems = [];
    let totalCurrentGiftQuantity = 0;
    
    for (const item of cart_items) {
      if (item.variant_id == variantId && isGiftLineItem(item)) {
        allGiftLineItems.push({ key: item.key, quantity: item.quantity });
        totalCurrentGiftQuantity += item.quantity;
      }
    }

    // Apply inventory limit to the cumulative target
    let desiredQuantity = Math.min(targets.targetQuantity, targets.inventoryLimit);
    desiredQuantity = Math.max(0, Math.floor(desiredQuantity));

    if (desiredQuantity === totalCurrentGiftQuantity && allGiftLineItems.length <= 1) {
      // Already in sync with single line item, nothing to do
      continue;
    }

    if (allGiftLineItems.length > 0) {
      // Gift line item(s) exist - consolidate to FIRST one and remove extras
      const primaryLineItem = allGiftLineItems[0];
      
      // Update the primary line item to the desired quantity
      giftQuantityUpdates.push({ key: primaryLineItem.key, quantity: desiredQuantity });
      
      // Remove all extra line items (set quantity to 0)
      for (let i = 1; i < allGiftLineItems.length; i++) {
        giftQuantityUpdates.push({ key: allGiftLineItems[i].key, quantity: 0 });
      }
    } else if (desiredQuantity > 0) {
      // No gift line item exists - ADD new one with _promo_gift property
      giftsToAdd.push({ 
        id: parseInt(variantId), 
        quantity: desiredQuantity,
        properties: { _promo_gift: 'true' }
      });
    }
  }

  //if there are gifts to add or update, handle them
  const hasAdds = giftsToAdd.length > 0;
  const hasUpdates = giftQuantityUpdates.length > 0;
  
  if (hasAdds || hasUpdates) {
    // Set lock to prevent concurrent operations
    isProcessingGifts = true;
    setCartDrawerLoading(true);
    const requests = [];

    if (hasAdds) {
      const addRequest = addToCart({ items: giftsToAdd })
        .catch((error) => {
          console.error('🎁 [PROMOTIONS] ❌ Error adding gifts to cart:', error);
        });
      requests.push(addRequest);
    }

    if (hasUpdates) {
      const updateRequest = updateGiftQuantities(giftQuantityUpdates)
        .catch((error) => {
          console.error('🎁 [PROMOTIONS] ❌ Error updating gift quantities:', error);
        });
      requests.push(updateRequest);
    }

    Promise.all(requests)
      .then(() => {
        // Fetch fresh cart data BEFORE dispatching events
        // This ensures window.cart_items is accurate when handlePromotions runs again
        return fetch('/cart.js').then(res => res.json());
      })
      .then((freshCart) => {
        // Update global cart state with fresh data
        window.cart_items = freshCart.items;
        previousCartItems = [...freshCart.items];
      })
      .catch((error) => {
        console.error('🎁 [PROMOTIONS] ❌ Error fetching fresh cart:', error);
      })
      .finally(() => {
        setCartDrawerLoading(false);
        // Release lock - keep handled=true to prevent immediate re-run
        isProcessingGifts = false;
        // Dispatch event to re-render cart UI
        dispatchEvent('cart:quantity-updated', document);
    });
  } else {
    setCartDrawerLoading(false);
  }

  //set handled to true so we don't add the gift again (until next change)
  handled = true;

  //hide the duplicate line item modifiers
  hideDuplicateLineItemModifiers();
}

// Calculate target gift quantity for Spend X Get Y (returns number, does not modify arrays)
function calculateSpendXGetYTarget(promoSettings) {
    const total_spent = promoSettings.cart_items.reduce((total, item) => {
        return total + (item.price * item.quantity);
    } , 0) + promoSettings.product_cost;

    const giftAmount = parseInt(promoSettings.gift.amount) || 1;
    let targetGiftSets = 0;

    if (promoSettings.minimum_spend > 0) {
        targetGiftSets = Math.floor(total_spent / promoSettings.minimum_spend);
    }

    if (promoSettings.max_gifts_per_order > 0) {
        const maxGiftSets = Math.floor(promoSettings.max_gifts_per_order / giftAmount);
        targetGiftSets = Math.min(targetGiftSets, maxGiftSets);
    }

    let targetGiftQuantity = targetGiftSets * giftAmount;
    // Note: inventory limit applied in reconciliation phase
    return Math.max(targetGiftQuantity, 0);
}

// Calculate target gift quantity for Buy X Get Y (returns number, does not modify arrays)
// gift.amount is used as a multiplier - e.g., if amount is 2, each qualifying purchase adds 2 gifts
function calculateBuyXGetYTarget(promoSettings) {
    const giftAmount = parseInt(promoSettings.gift.amount) || 1; // Quantity multiplier per gift
    
    let targetGiftSets = 0;

    if (promoSettings.x_id == promoSettings.y_variant_id) {
        targetGiftSets = Math.floor(promoSettings.qualifying_item_quantity / 2);
    } else {
        if (promoSettings.qualifying_item_quantity > 0 && promoSettings.minimum_spend > 0) {
            const spent_on_item = promoSettings.qualifying_item_cost * promoSettings.qualifying_item_quantity;
            targetGiftSets = Math.floor(spent_on_item / promoSettings.minimum_spend);
        }
    }

    if (promoSettings.max_gifts_per_order > 0) {
        const maxGiftSets = Math.floor(promoSettings.max_gifts_per_order / giftAmount);
        targetGiftSets = Math.min(targetGiftSets, maxGiftSets);
    }

    let targetGiftQuantity = targetGiftSets * giftAmount;
    // Note: inventory limit applied in reconciliation phase
    return Math.max(targetGiftQuantity, 0);
}

const hideDuplicateLineItemModifiers = () => {
    let itemsToHideControls = [];
    const cartItems = window.cart_items;

    for(let i = 0; i < cartItems.length; i++){
      let currentItem = cartItems[i];

      // Hide controls for gift items (they're managed automatically)
      if (isGiftLineItem(currentItem)) {
        itemsToHideControls.push(currentItem.key);
      }

      // Also hide controls for duplicate discounted items (original behavior)
      for(let j = i + 1; j < cartItems.length; j++){
        let nextItem = cartItems[j];
        
        if(currentItem.variant_id == nextItem.variant_id){
          if(currentItem.discounts.length > 0){
            itemsToHideControls.push(currentItem.key);
          }
          if(nextItem.discounts.length > 0){
            itemsToHideControls.push(nextItem.key);
          }
        }
      }
    }    

    itemsToHideControls.forEach((item) => {
     for (const itemDiv of document.querySelectorAll(`[data-line-item-key="${item}"]`)) {
        const decrementSpan = itemDiv.querySelector('[data-decrement]');
        const incrementSpan = itemDiv.querySelector('[data-increment]');
        const removeItem = itemDiv.querySelector('[data-remove-button]');
        if (decrementSpan) decrementSpan.classList.add('hidden');
        if (incrementSpan) incrementSpan.classList.add('hidden');
        if (removeItem) removeItem.classList.add('hidden');
      }

    });

}

// Check if a line item is a promotional gift (has _promo_gift property)
function isGiftLineItem(item) {
  if (!item || !item.properties) {
    return false;
  }
  return item.properties._promo_gift === 'true';
}

function groupItems(items) {
  let groupedItems = {};

  if (!Array.isArray(items)) {
    return groupedItems;
  }

  for (const item of items) {
    if (!item) {
      continue;
    }

    const keys = [];

    if (item.variant_id != null) {
      keys.push(item.variant_id);
    }

    if (item.product_id != null) {
      keys.push(item.product_id);
    }

    for (const key of keys) {
      if (groupedItems[key]) {
        groupedItems[key].quantity += item.quantity;
    } else {
        groupedItems[key] = { quantity: item.quantity };
      }
    }
  }

  return groupedItems;
}

function setCartDrawerLoading(isLoading) {
  const cartDrawer = document.querySelector('[data-cart-drawer]');
  const loadingOverlay = cartDrawer?.querySelector('[data-cart-drawer-loading]');

  if (!cartDrawer || !loadingOverlay) {
    return;
  }

  if (isLoading) {
    cartDrawer.classList.add('cart-drawer--loading');
    loadingOverlay.removeAttribute('hidden');
  } else {
    cartDrawer.classList.remove('cart-drawer--loading');
    loadingOverlay.setAttribute('hidden', 'hidden');
  }
}

function updateGiftQuantities(updates) {
  if (!updates || updates.length === 0) {
    return Promise.resolve();
  }

  // Use line item keys for precise control over multiple line items of same variant
  const body = { updates: {} };
  updates.forEach(({ key, quantity }) => {
    body.updates[key] = quantity;
  });

  return fetch('/cart/update.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  })
    .then((response) => response.json());
  // Note: cart:quantity-updated is dispatched after all operations complete in handlePromotions
}


function noPromotionalItemsChanged(promotions, currentItems, previousItems) {
  if (Object.keys(previousItems).length === 0) {
    return false;
  }

  for (const promotion of promotions) {
    const currentQty = currentItems[promotion.x_id]?.quantity || 0;
    const previousQty = previousItems[promotion.x_id]?.quantity || 0;

    // Trigger recalculation if quantity changed in either direction
    if (currentQty !== previousQty) {
        return false;
      }

    // Also check if item was added or removed entirely
    const wasPresent = previousItems[promotion.x_id] !== undefined;
    const isPresent = currentItems[promotion.x_id] !== undefined;
    if (wasPresent !== isPresent) {
        return false;
    }
  }
  return true;
}

export { hideDuplicateLineItemModifiers, handlePromotions, isPromotionalItem };
